import type { ButtonHTMLAttributes } from "react";

import { cn } from "../lib/cn";
import { useScrollReach } from "../lib/use-scroll-reach";

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  /** How many records the filter would show — "Hens 36". Drawn lighter than the label. */
  count?: number | undefined;
}

/**
 * One filter in the horizontally scrolling chip row.
 *
 * The active chip is the strong sage as a solid fill under light text — the one place a
 * list screen spends it. Borderless: the fill separates the active chip and a recessed
 * ground separates the rest, so an outline would only add a third edge.
 *
 * 38px rather than the 44px touch minimum, because chips sit in a scrolling row where
 * the whole row is the target and the spacing around them carries the rest.
 */
export function FilterChip({
  className,
  active = false,
  count,
  children,
  ...props
}: FilterChipProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        "inline-flex h-pill shrink-0 items-center gap-1.5 rounded-control px-3.75",
        "text-pill transition-colors",
        active
          ? "bg-primary-strong text-on-primary-strong"
          : "bg-surface-container text-on-surface-body hover:bg-surface-container-high",
        className,
      )}
      {...props}
    >
      {children}
      {count === undefined ? null : (
        <span className="font-semibold opacity-75" data-numeric>
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * The scrolling row itself.
 *
 * Scrollbar hidden — it is a swipe affordance, not a control — and the edge it can
 * still be pulled from fades instead, so a row of chips that ends flush with the
 * screen does not read as the whole set.
 */
export function FilterChipRow({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { ref, fade } = useScrollReach<HTMLDivElement>(children);

  return (
    <div
      ref={ref}
      role="group"
      className={cn(
        "flex gap-2 overflow-x-auto",
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        fade,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
